import Cookies from 'js-cookie';
import { useState, useEffect } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { loadFacebookSDK, loadGoogleClientSDK } from './Authentication.utils';
import { CURRENT_USER } from '../App.gql';
import { getCookieDomain } from '../../util/util';
import { trackEvent } from '../../util/track';

/**
 * React hook for safely interacting with the Facebook SDK.
 * Don't call window.FB until this hook returns `true`.
 * @returns {Boolean} `true` if Facebook SDK is ready, otherwise `false`.
 */
export const useFacebookSdk = () => {
  const getFbjsTag = () => document.getElementById('facebook-jssdk');
  const [isReady, setIsReady] = useState(
    getFbjsTag()?.getAttribute('data-status') === 'ready'
  );

  useEffect(() => {
    // Short circuit if already initialized
    if (isReady) return;

    if (!window.fbAsyncInit) {
      // First instance; setup FB SDK
      window.fbAsyncInit = () => {
        window.FB.init({
          appId: '1680767455471626',
          cookie: true,
          version: 'v11.0'
        });

        document
          .getElementById('facebook-jssdk')
          .setAttribute('data-status', 'ready');
      };
    }

    // Used to ensure all hooks on initial render are updated when SDK is ready to use.
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (
          m.type === 'attributes' &&
          m.target.getAttribute('data-status') === 'ready'
        ) {
          setIsReady(true);
          observer.disconnect();
        }
      });
    });

    loadFacebookSDK();
    observer.observe(getFbjsTag(), { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return isReady;
};

/**
 * React hook for safely interacting with the Google Client SDK.
 * Don't call window.google until this hook returns `true`.
 * @returns {Boolean} `true` if Google Client SDK is ready, otherwise `false`.
 */
export const useGoogleSdk = () => {
  const getGoogleClientTag = () => document.getElementById('google-clientsdk');
  const [isReady, setIsReady] = useState(
    getGoogleClientTag()?.getAttribute('data-status') === 'ready'
  );

  useEffect(() => {
    // Short circuit if already initialized
    if (isReady) return;

    if (!window.google) {
      window.onGoogleLibraryLoad = () => {
        document
          .getElementById('google-clientsdk')
          .setAttribute('data-status', 'ready');
      };
    }

    // Used to ensure all hooks on initial render are updated when SDK is ready to use.
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (
          m.type === 'attributes' &&
          m.target.getAttribute('data-status') === 'ready'
        ) {
          setIsReady(true);
          observer.disconnect();
        }
      });
    });

    loadGoogleClientSDK();
    observer.observe(getGoogleClientTag(), { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return isReady;
};

export const useIdentity = () => {
  const client = useApolloClient();
  const { data } = useQuery(CURRENT_USER);
  const [loading, setIsloading] = useState(true);
  const user = data?.user || {};

  useEffect(() => {
    setIsloading(false);
  }, []);

  const identifyUser = (attr = {}) => {
    const identity = { ...user, ...attr };

    client.writeQuery({
      query: CURRENT_USER,
      data: {
        user: identity
      }
    });

    Cookies.set('BOM-USER', JSON.stringify(identity), {
      domain: getCookieDomain(),
      expires: 365
    });

    trackEvent({ event: 'identifyUser', identity });
  };

  const eraseUser = () => {
    client.writeQuery({
      query: CURRENT_USER,
      data: {
        user: {}
      }
    });

    Cookies.remove('BOM-USER', { domain: getCookieDomain() });
    Cookies.remove('BOM-CAT', { domain: getCookieDomain() });
    Cookies.remove('BOM-SESSION_INIT', { domain: getCookieDomain() });
  };

  return { user, loading, identifyUser, eraseUser };
};

export default {};
